import { supabase } from './supabase'
import { Database } from '@/types/supabase'

type Profile = Database['public']['Tables']['profiles']['Row']
type ProfileUpdate = Database['public']['Tables']['profiles']['Update']

interface ProfileResult<T> {
  data: T | null
  error: string | null
}

/**
 * Get a user profile by ID
 */
async function getProfileById(id: string): Promise<ProfileResult<Profile>> {
  if (!id) return { data: null, error: 'User ID is required' }

  try {
    const { data, error } = await supabase.from('profiles').select('*').eq('id', id).single()

    if (error) return { data: null, error: error.message }

    return { data, error: null }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unknown error occurred fetching profile',
    }
  }
}

/**
 * Get a user profile by username
 */
async function getProfileByUsername(username: string): Promise<ProfileResult<Profile>> {
  if (!username) return { data: null, error: 'Username is required' }

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('username', username)
      .single()

    if (error) return { data: null, error: error.message }

    return { data, error: null }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unknown error occurred fetching profile',
    }
  }
}

/**
 * Update a user profile
 */
async function updateProfile(profile: ProfileUpdate): Promise<ProfileResult<Profile>> {
  if (!profile.id) return { data: null, error: 'User ID is required' }

  // Set updated_at to current timestamp
  const updatedProfile = {
    ...profile,
    updated_at: new Date().toISOString(),
  }

  try {
    const { data, error } = await supabase
      .from('profiles')
      .update(updatedProfile)
      .eq('id', profile.id)
      .select()
      .single()

    if (error) return { data: null, error: error.message }

    return { data, error: null }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unknown error occurred updating profile',
    }
  }
}

/**
 * Upload a profile avatar
 */
async function uploadAvatar(userId: string, file: File): Promise<ProfileResult<{ path: string }>> {
  if (!userId) return { data: null, error: 'User ID is required' }
  if (!file) return { data: null, error: 'File is required' }

  const fileExt = file.name.split('.').pop()
  const filePath = `${userId}/avatar.${fileExt}`

  try {
    // Upload the file to Supabase Storage with cache control
    const { error: uploadError, data: uploadData } = await supabase.storage
      .from('users')
      .upload(filePath, file, {
        upsert: true,
        cacheControl: 'no-cache, no-store, must-revalidate', // Prevent caching
        contentType: file.type, // Ensure correct content type
      })

    if (uploadError) {
      return { data: null, error: uploadError.message }
    }

    // Get the public URL with a timestamp to prevent caching
    const timestamp = new Date().getTime()
    const { data } = supabase.storage.from('users').getPublicUrl(`${filePath}?t=${timestamp}`)

    // Force browser to reload the image by creating a temporary image object
    try {
      // Get the existing profile to check if there's a previous avatar
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('avatar_url')
        .eq('id', userId)
        .single()

      if (existingProfile?.avatar_url) {
        // Force a reload of the old URL to invalidate any cached versions
        const img = new Image()
        img.src = `${existingProfile.avatar_url}?force_reload=${timestamp}`
      }
    } catch (invalidateError) {
      // Non-critical error, we can continue
    }

    // Update the user's profile with the new avatar URL
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: data.publicUrl })
      .eq('id', userId)

    if (updateError) {
      return { data: null, error: updateError.message }
    }

    return { data: { path: data.publicUrl }, error: null }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unknown error occurred uploading avatar',
    }
  }
}

export const profileService = {
  getProfileById,
  getProfileByUsername,
  updateProfile,
  uploadAvatar,
}
